<script setup>
import {tvDrawPages, tvWhen} from '~/utils/tvDisplay';

// A tournament: group tables while the groups are on, then the last knockout
// rounds side by side. Winners are decided by points, never the result text.
const props = defineProps({
  panel: {type: Object, required: true},
  page: {type: Number, default: 0},
});
// wide, column or portrait (set by the panel's frame)
const mode = inject('tvMode', ref('wide'));

const current = computed(() => tvDrawPages(props.panel.draw, mode.value)[props.page] ?? {groups: []});
const winner = (f) => (f.homePoints == null || f.awayPoints == null || f.homePoints === f.awayPoints)
    ? null : (f.homePoints > f.awayPoints ? 'home' : 'away');
const sorted = (fixtures) => [...(fixtures ?? [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
</script>

<template>
  <div v-if="current.knockout" class="tv-ko">
    <div v-for="stage in current.knockout" :key="stage.name" class="tv-ko-round">
      <div class="tv-ko-name">{{ stage.name }}</div>
      <div class="tv-ko-matches">
        <div v-for="(f, i) in sorted(stage.fixtures)" :key="i" class="tv-ko-match">
          <div class="tv-ko-side" :class="{'tv-ko-side--won': winner(f) === 'home'}">
            <span class="tv-ko-team">{{ f.homeTeamName ?? 'To be decided' }}</span>
            <span class="tv-ko-score">{{ f.homeResult }}</span>
          </div>
          <div class="tv-ko-side" :class="{'tv-ko-side--won': winner(f) === 'away'}">
            <span class="tv-ko-team">{{ f.awayTeamName ?? 'To be decided' }}</span>
            <span class="tv-ko-score">{{ f.awayResult }}</span>
          </div>
          <div v-if="f.games?.length" class="tv-ko-games">{{ f.games.map(g => `${g.home}–${g.away}`).join('  ') }}</div>
          <div v-if="f.homeResult == null && f.scheduledAt" class="tv-ko-when">{{ tvWhen(f.scheduledAt) }}</div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="tv-groups" :class="{'tv-groups--one': current.groups.length === 1}">
    <div v-for="group in current.groups" :key="group.name" class="tv-group">
      <div class="tv-group-name">{{ group.name }}</div>
      <div v-for="row in (group.groupRows ?? []).slice(0, 6)" :key="row.position + row.teamName" class="tv-group-row" :class="{'tv-group-row--top': row.position === 1}">
        <span class="tv-group-pos">{{ row.position }}</span>
        <span class="tv-group-team">{{ row.teamName }}</span>
        <span class="tv-group-pts">{{ row.totalPoints }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tv-groups {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px 56px;
}

.tv-groups--one {
  grid-template-columns: 1fr;
}

.tv-group-name {
  font-family: var(--tv-display);
  font-weight: 700;
  font-size: 34px;
  color: var(--tv-ink);
  padding-bottom: 8px;
  border-bottom: 2px solid var(--tv-line);
}

.tv-group-row {
  display: grid;
  grid-template-columns: 56px 1fr 90px;
  align-items: center;
  height: 50px;
  padding: 0 16px;
  border-bottom: 1px solid var(--tv-line);
  font-size: 28px;
  color: var(--tv-ink);
}

.tv-group-row--top {
  box-shadow: inset 6px 0 0 var(--tv-accent);
  background: var(--tv-lead);
}

.tv-group-pos {
  font-family: var(--tv-display);
  font-weight: 700;
  font-size: 32px;
  color: var(--tv-muted);
}

.tv-group-team {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tv-group-pts {
  text-align: right;
  font-family: var(--tv-display);
  font-weight: 700;
  font-size: 34px;
}

.tv-ko {
  display: flex;
  gap: 48px;
  height: 610px;
}

.tv-ko-round {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.tv-ko-name {
  font-family: var(--tv-display);
  font-weight: 700;
  font-size: 34px;
  color: var(--tv-muted);
  padding-bottom: 12px;
}

.tv-ko-matches {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: 12px;
}

.tv-ko-match {
  border-radius: 10px;
  border: 2px solid var(--tv-line);
  background: var(--tv-panel);
  overflow: hidden;
}

.tv-ko-side {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 6px 18px;
  font-size: 26px;
  color: var(--tv-muted);

  & + & {
    border-top: 1px solid var(--tv-line);
  }
}

.tv-ko-side--won {
  color: var(--tv-ink);
  font-weight: 700;
  box-shadow: inset 6px 0 0 var(--tv-accent);
}

.tv-ko-team {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tv-ko-score {
  font-family: var(--tv-display);
  font-weight: 700;
  font-size: 30px;
}

.tv-ko-when {
  padding: 4px 18px 6px;
  font-size: 20px;
  color: var(--tv-accent-text);
  border-top: 1px solid var(--tv-line);
}

:global(.tv-narrow .tv-groups) {
  grid-template-columns: 1fr;
}

:global(.tv-narrow .tv-ko) {
  height: 1400px;
}

.tv-ko-games {
  padding: 2px 18px 4px;
  font-family: var(--tv-display);
  font-size: 24px;
  color: var(--tv-muted);
  border-top: 1px solid var(--tv-line);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}
</style>
